/**
 * 1.1 — Login · `telas/1.1-login.html`
 *
 * Sete views numa coluna só. Três vêm do protótipo e têm baseline para comparar
 * (login · esqueci · enviado); as **quatro novas** — redefinir senha, aceitar
 * convite, etapa 2FA e enrolamento — não existem no HTML e nascem reusando esta
 * MESMA coluna: mesmo grid de duas colunas, mesmo painel ink, mesmos componentes de
 * campo, botão e banner. Nenhum layout novo é inventado; o que muda é o conteúdo.
 *
 * **A credencial fica na memória desta página, e em nenhum servidor.** Quem precisa
 * do segundo fator digita e-mail e senha uma vez; a etapa seguinte reenvia o que já
 * está aqui, junto do código. É o que permite exigir o fator ANTES de existir
 * qualquer cookie — sem estado intermediário no servidor, não há sessão parcial.
 *
 * Erro de credencial é **sempre genérico** (DS-37): a tela nunca diz qual campo
 * errou, nem que o e-mail não existe, nem que a conta está suspensa.
 *
 * **"Confiar neste dispositivo" viaja com a credencial, pelo mesmo motivo.** A caixa
 * é marcada na view de login, mas quem tem segundo fator só abre sessão duas telas
 * depois — no `2fa/verify` ou no `2fa/confirmar`. A preferência precisa chegar
 * ATÉ LÁ: enquanto ela parava no primeiro POST, marcar a caixa não mudava nada para
 * quem usa 2FA, que é justamente quem tem alçada. Ela não é guardada em lugar
 * nenhum do navegador — vive neste estado de React, como a senha, e some com a
 * página. Confiar num dispositivo é uma sessão mais longa, nunca um atalho que
 * dispense o segundo fator.
 */
import { useEffect, useRef, useState } from 'react'
import {
  email as emailDoContrato,
  senha as senhaDoContrato,
  DIAS_DE_DISPOSITIVO_CONFIAVEL,
  MINIMO_DA_SENHA,
} from '@dv/contracts'
import { ErroDaApi } from '../api/cliente.ts'
import * as api from '../api/recursos.ts'
import {
  Banner,
  BotaoPrimario,
  BotaoSecundario,
  Campo,
  useFocoNoPrimeiroInvalido,
} from '../componentes/formulario.tsx'
import { Icone } from '../shell/icone.tsx'
import { Qr } from '../componentes/qr.tsx'
import { useToast } from '../componentes/toast.tsx'
import { destinoSeguro } from './entrada-com-retorno.ts'

type View = 'login' | 'esqueci' | 'enviado' | 'verificacao2fa' | 'enrolamento2fa' | 'redefinir' | 'convite'

interface Erros {
  readonly email?: string | undefined
  readonly senha?: string | undefined
  readonly codigo?: string | undefined
  readonly banner?: string | undefined
}

/**
 * O texto do banner quando a falha não trouxe envelope da API: servidor mudo, rede
 * caída. O título do toast já diz esta mesma frase, e repeti-la na descrição encheria
 * o cartão com uma linha que não acrescenta nada — por isso a descrição do toast só
 * aparece quando a API mandou uma mensagem própria.
 */
const FALHA_SEM_RESPOSTA = 'Não foi possível entrar.'

/**
 * Para onde ir depois de entrar: a rota de origem, se ela é deste app, ou o cockpit. A guarda
 * compara a origem (DS-638): `//outro.site` e companhia caem no cockpit.
 */
function destinoDeEntrada(): string {
  return destinoSeguro(new URLSearchParams(window.location.search).get('de'), window.location.origin)
}

export function Entrar() {
  const parametros = new URLSearchParams(window.location.search)
  const tokenDeReset = parametros.get('reset')
  const tokenDeConvite = parametros.get('convite')

  const [view, setView] = useState<View>(
    tokenDeReset !== null ? 'redefinir' : tokenDeConvite !== null ? 'convite' : 'login',
  )
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [senhaNova, setSenhaNova] = useState('')
  const [codigo, setCodigo] = useState('')
  const [manterConectado, setManterConectado] = useState(false)
  const [mostrarSenha, setMostrarSenha] = useState(false)
  const [erros, setErros] = useState<Erros>({})
  useFocoNoPrimeiroInvalido(erros)
  const [enviando, setEnviando] = useState(false)
  const [fator, setFator] = useState<{ uri: string; segredo: string; codigos: string[] } | null>(null)
  const [codigosGuardados, setCodigosGuardados] = useState(false)

  const campoDeEmail = useRef<HTMLInputElement>(null)
  const campoDeSenha = useRef<HTMLInputElement>(null)
  const avisar = useToast()

  // Sessão viva chegando na tela de entrada vai direto ao cockpit (spec 1.1).
  useEffect(() => {
    if (tokenDeReset !== null || tokenDeConvite !== null) return
    void api
      .sessaoAtual()
      .then((s) => {
        if (s.email !== null) window.location.replace(destinoDeEntrada())
      })
      .catch(() => {
        // Sem sessão é o caso normal desta tela — nada a fazer.
      })
  }, [tokenDeReset, tokenDeConvite])

  function limparErro(campo: keyof Erros): void {
    setErros((atuais) => ({ ...atuais, [campo]: undefined, banner: undefined }))
  }

  /**
   * Falha de envio: banner NO formulário e toast na pilha (spec 1.1 §Estados).
   *
   * As duas coisas não são a mesma dita duas vezes. O banner FICA: é o registro no
   * topo da coluna, e continua ali enquanto a pessoa corrige a senha. O toast
   * INTERROMPE: ele nasce com `role="alert"` e é o único dos dois que um leitor de
   * tela anuncia sem que o foco esteja no topo do formulário — quem apertou "Entrar"
   * está com o foco no botão, no pé do cartão, e não ouvia nada acontecer.
   *
   * Erro de CAMPO continua só inline: o código do segundo fator já está debaixo do
   * olho de quem acabou de digitá-lo, e não precisa de eco no canto da tela.
   *
   * E o toast é só do erro de AUTENTICAÇÃO. Este `tratar` é o tratador de TODAS as
   * views desta coluna — pedir o link de recuperação, redefinir a senha, aceitar o
   * convite —, e nessas três "Não foi possível entrar" seria falso: ninguém tentou
   * entrar. Ali o banner continua sozinho, como hoje.
   */
  function tratar(causa: unknown): void {
    const erro = causa instanceof ErroDaApi ? causa : new ErroDaApi('INTERNAL', FALHA_SEM_RESPOSTA)
    const doCodigo = erro.doCampo('codigo')
    if (doCodigo !== undefined) {
      setErros({ codigo: doCodigo })
      return
    }
    setErros({ banner: erro.message })
    if (view !== 'login' && view !== 'verificacao2fa' && view !== 'enrolamento2fa') return
    avisar({
      tom: 'erro',
      titulo: 'Não foi possível entrar',
      ...(erro.message === FALHA_SEM_RESPOSTA ? {} : { descricao: erro.message }),
    })
  }

  async function comEnvio(acao: () => Promise<void>): Promise<void> {
    if (enviando) return
    setEnviando(true)
    try {
      await acao()
    } catch (causa) {
      tratar(causa)
    } finally {
      setEnviando(false)
    }
  }

  /** Validação local antes do POST — e-mail com formato, senha obrigatória. */
  function credencialLocalValida(): boolean {
    const problemas: Erros = {
      ...(emailDoContrato.safeParse(email.trim()).success ? {} : { email: 'Informe um e-mail válido.' }),
      ...(senha === '' ? { senha: 'Informe a senha.' } : {}),
    }
    setErros(problemas)
    // O foco vai ao PRIMEIRO campo inválido: mandar a pessoa procurar o erro na
    // tela é o mesmo que não apontá-lo.
    if (problemas.email !== undefined) campoDeEmail.current?.focus()
    else if (problemas.senha !== undefined) campoDeSenha.current?.focus()
    return problemas.email === undefined && problemas.senha === undefined
  }

  async function seguirDe(proximoPasso: string, credenciais = { email: email.trim(), senha }): Promise<void> {
    if (proximoPasso === 'entrou') {
      window.location.replace(destinoDeEntrada())
      return
    }
    if (proximoPasso === 'verificacao_2fa') {
      setView('verificacao2fa')
      return
    }
    // O token acabou de definir as credenciais; não dependemos de setState ter renderizado.
    setView('enrolamento2fa')
    setFator(null)
    setErros({})
    const novo = await api.enrolarSegundoFator(credenciais)
    setFator({ uri: novo.uri, segredo: novo.segredo, codigos: novo.codigosDeRecuperacao })
    setCodigosGuardados(false)
    setView('enrolamento2fa')
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <PainelInk />

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8">
            <span className="text-[22px] font-semibold tracking-tight text-navy">digitalview</span>
          </div>

          {view === 'login' && (
            <section data-teste="view-login">
              <p className="eyebrow text-muted mb-2">Acesso interno</p>
              <h2 className="text-[26px] font-semibold tracking-tight text-navy leading-tight">
                Entrar na plataforma
              </h2>
              <p className="text-sm text-muted mt-1.5 mb-7">Use suas credenciais corporativas.</p>

              {erros.banner !== undefined && <Banner>{erros.banner}</Banner>}

              <form
                className="space-y-4"
                noValidate
                onSubmit={(evento) => {
                  evento.preventDefault()
                  if (!credencialLocalValida()) return
                  void comEnvio(async () => {
                    const r = await api.entrar({ email: email.trim(), senha, manterConectado })
                    await seguirDe(r.proximoPasso)
                  })
                }}
              >
                <Campo
                  classe="md:!h-11"
                  ref={campoDeEmail}
                  name="email"
                  type="email"
                  rotulo="E-mail"
                  autoComplete="username"
                  value={email}
                  erro={erros.email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    limparErro('email')
                  }}
                />
                <Campo
                  classe="md:!h-11"
                  ref={campoDeSenha}
                  name="senha"
                  type={mostrarSenha ? 'text' : 'password'}
                  rotulo="Senha"
                  autoComplete="current-password"
                  value={senha}
                  erro={erros.senha}
                  onChange={(e) => {
                    setSenha(e.target.value)
                    limparErro('senha')
                  }}
                  acessorio={
                    <button
                      type="button"
                      className="touch-44 inline-flex items-center text-[12px] font-medium text-navy dv-link"
                      onClick={() => {
                        setErros({})
                        setView('esqueci')
                      }}
                    >
                      Esqueci a senha
                    </button>
                  }
                  depoisDoCampo={
                    <button
                      type="button"
                      onClick={() => {
                        setMostrarSenha((v) => !v)
                      }}
                      className="touch-44 absolute right-1 md:right-2 top-1/2 -translate-y-1/2 h-11 w-11 md:h-8 md:w-8 grid place-items-center text-muted hover:text-text2"
                      aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                      title={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                    >
                      <Icone nome={mostrarSenha ? 'eye-off' : 'eye'} className="w-4 h-4" />
                    </button>
                  }
                />
                <label className="flex items-center gap-2.5 text-sm text-text2">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-hairline accent-navy"
                    checked={manterConectado}
                    onChange={(e) => {
                      setManterConectado(e.target.checked)
                    }}
                  />{' '}
                  Confiar neste dispositivo por {String(DIAS_DE_DISPOSITIVO_CONFIAVEL)} dias
                </label>
                <BotaoPrimario
                  type="submit"
                  className="w-full min-h-[44px]"
                  carregando={enviando}
                  rotuloDeCarregamento="Entrando…"
                >
                  <span className="lbl">Entrar</span> <Icone nome="arrow-right" className="w-4 h-4" />
                </BotaoPrimario>
              </form>
            </section>
          )}

          {view === 'esqueci' && (
            <section data-teste="view-esqueci">
              <Voltar
                aoVoltar={() => {
                  setErros({})
                  setView('login')
                }}
              />
              <p className="eyebrow text-muted mb-2">Recuperação</p>
              <h2 className="text-[26px] font-semibold tracking-tight text-navy leading-tight">Esqueci a senha</h2>
              <p className="text-sm text-muted mt-1.5 mb-7">
                Enviaremos um link de redefinição para o seu e-mail.
              </p>
              {erros.banner !== undefined && <Banner>{erros.banner}</Banner>}
              <form
                className="space-y-4"
                noValidate
                onSubmit={(evento) => {
                  evento.preventDefault()
                  if (!emailDoContrato.safeParse(email.trim()).success) {
                    setErros({ email: 'Informe um e-mail válido.' })
                    campoDeEmail.current?.focus()
                    return
                  }
                  void comEnvio(async () => {
                    await api.esqueciSenha(email.trim())
                    setView('enviado')
                  })
                }}
              >
                <Campo
                  classe="md:!h-11"
                  ref={campoDeEmail}
                  name="email-recuperacao"
                  type="email"
                  rotulo="E-mail"
                  placeholder="voce@digitalview.com.br"
                  autoComplete="username"
                  value={email}
                  erro={erros.email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    limparErro('email')
                  }}
                />
                <BotaoPrimario
                  type="submit"
                  className="w-full min-h-[44px]"
                  carregando={enviando}
                  rotuloDeCarregamento="Enviando…"
                >
                  Enviar link
                </BotaoPrimario>
              </form>
            </section>
          )}

          {view === 'enviado' && (
            <section className="text-center" data-teste="view-enviado">
              <div className="w-14 h-14 rounded-2xl bg-navy/[.06] grid place-items-center text-navy mx-auto mb-4">
                <Icone nome="mail-check" className="w-7 h-7" />
              </div>
              <h2 className="text-[22px] font-semibold tracking-tight text-navy">Verifique seu e-mail</h2>
              <p className="text-sm text-muted mt-2">
                Se houver uma conta associada, você receberá as instruções em instantes.
              </p>
              <BotaoSecundario
                className="mt-6 w-full min-h-[44px]"
                onClick={() => {
                  setErros({})
                  setView('login')
                }}
              >
                Voltar ao login
              </BotaoSecundario>
            </section>
          )}

          {view === 'verificacao2fa' && (
            <section data-teste="view-verificacao-2fa">
              <Voltar
                aoVoltar={() => {
                  setCodigo('')
                  setErros({})
                  setView('login')
                }}
              />
              <p className="eyebrow text-muted mb-2">Verificação em duas etapas</p>
              <h2 className="text-[26px] font-semibold tracking-tight text-navy leading-tight">Informe o código</h2>
              <p className="text-sm text-muted mt-1.5 mb-7">
                Abra o aplicativo autenticador e digite o código de 6 dígitos.
              </p>
              {erros.banner !== undefined && <Banner>{erros.banner}</Banner>}
              <form
                className="space-y-4"
                noValidate
                onSubmit={(evento) => {
                  evento.preventDefault()
                  void comEnvio(async () => {
                    const r = await api.verificarSegundoFator({
                      email: email.trim(),
                      senha,
                      codigo,
                      manterConectado,
                    })
                    await seguirDe(r.proximoPasso)
                  })
                }}
              >
                <CampoDeCodigo
                  valor={codigo}
                  erro={erros.codigo}
                  aoMudar={(v) => {
                    setCodigo(v)
                    limparErro('codigo')
                  }}
                />
                <BotaoPrimario
                  type="submit"
                  className="w-full min-h-[44px]"
                  carregando={enviando}
                  rotuloDeCarregamento="Verificando…"
                >
                  Verificar
                </BotaoPrimario>
              </form>
              {/* `text-balance` mora só aqui, e de propósito: esta frase quebrava em duas
                  linhas muito desiguais, e equilibrar a quebra é o único ajuste que ela pede.
                  Não é padrão das microcopies centralizadas — não estender nem remover. */}
              <p className="text-[12px] text-muted mt-6 text-center text-balance">
                Sem acesso ao aplicativo? Use um dos códigos de recuperação que você guardou.
              </p>
            </section>
          )}

          {view === 'enrolamento2fa' && fator === null && (
            <section data-teste="view-enrolamento-2fa-pendente">
              <h2 className="text-[26px] font-semibold tracking-tight text-navy leading-tight">
                Configurar verificação em duas etapas
              </h2>
              <p className="text-sm text-muted mt-1.5 mb-6">
                Prepare o aplicativo autenticador para concluir seu acesso.
              </p>
              {erros.banner !== undefined && <Banner>{erros.banner}</Banner>}
              <BotaoPrimario
                className="w-full min-h-[44px]"
                carregando={enviando}
                rotuloDeCarregamento="Preparando…"
                onClick={() => { void comEnvio(() => seguirDe('enrolamento_2fa')) }}
              >
                Tentar novamente
              </BotaoPrimario>
            </section>
          )}

          {view === 'enrolamento2fa' && fator !== null && (
            <section data-teste="view-enrolamento-2fa">
              <p className="eyebrow text-muted mb-2">Segurança</p>
              <h2 className="text-[26px] font-semibold tracking-tight text-navy leading-tight">
                Configurar verificação em duas etapas
              </h2>
              <p className="text-sm text-muted mt-1.5 mb-6">
                Seu nível de acesso exige o segundo fator. Cadastre o aplicativo autenticador para entrar.
              </p>

              <div className="rounded-[10px] border border-hairline bg-white p-4 mb-4">
                <p className="eyebrow text-muted mb-2">1. Cadastre no aplicativo</p>
                <p className="text-[13px] text-text2 mb-3">
                  Abra o aplicativo autenticador (Google Authenticator, 1Password, Authy) e aponte a
                  câmera para o código.
                </p>

                <Qr
                  texto={fator.uri}
                  rotulo="Código para leitura pelo aplicativo autenticador"
                  className="w-40 h-40 mx-auto rounded-[6px]"
                />

                {/* O texto continua aqui, e não é sobra: quem abre o sistema NO celular
                    não tem como apontar a câmera para a própria tela. */}
                <details className="mt-3 group">
                  <summary className="text-[12px] text-muted cursor-pointer select-none hover:text-text2">
                    Não consegue ler o código? Digite a chave
                  </summary>
                  <p
                    className="text-[13px] text-text2 break-all dv-codigo mt-2"
                    data-teste="segredo-2fa"
                  >
                    {fator.segredo}
                  </p>
                </details>
              </div>

              <div className="rounded-[10px] border border-amber-200 bg-amber-50 p-4 mb-4">
                <p className="eyebrow text-amber-800 mb-2">2. Guarde os códigos de recuperação</p>
                <p className="text-[12px] text-amber-800 mb-2">
                  Eles aparecem uma única vez e são a sua saída se perder o aparelho.
                </p>
                <ul className="grid grid-cols-2 gap-1 text-[12px] dv-codigo text-amber-900">
                  {fator.codigos.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
                <label className="flex items-center gap-2.5 text-[12px] text-amber-900 mt-3">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-amber-300 accent-navy"
                    checked={codigosGuardados}
                    onChange={(e) => {
                      setCodigosGuardados(e.target.checked)
                    }}
                  />
                  Guardei os códigos em lugar seguro
                </label>
              </div>

              {erros.banner !== undefined && <Banner>{erros.banner}</Banner>}

              <form
                className="space-y-4"
                noValidate
                onSubmit={(evento) => {
                  evento.preventDefault()
                  void comEnvio(async () => {
                    const r = await api.confirmarSegundoFator({
                      email: email.trim(),
                      senha,
                      codigo,
                      manterConectado,
                    })
                    await seguirDe(r.proximoPasso)
                  })
                }}
              >
                <p className="eyebrow text-muted">3. Confirme o código do aplicativo</p>
                <CampoDeCodigo
                  valor={codigo}
                  erro={erros.codigo}
                  aoMudar={(v) => {
                    setCodigo(v)
                    limparErro('codigo')
                  }}
                />
                <BotaoPrimario
                  type="submit"
                  className="w-full min-h-[44px]"
                  carregando={enviando}
                  disabled={!codigosGuardados}
                  rotuloDeCarregamento="Confirmando…"
                >
                  Confirmar e entrar
                </BotaoPrimario>
              </form>
            </section>
          )}

          {(view === 'redefinir' || view === 'convite') && (
            <DefinicaoDeSenha
              modo={view}
              senha={senhaNova}
              erro={erros.senha}
              banner={erros.banner}
              enviando={enviando}
              aoMudarSenha={(v) => {
                setSenhaNova(v)
                limparErro('senha')
              }}
              aoEnviar={() => {
                const conferida = senhaDoContrato.safeParse(senhaNova)
                if (!conferida.success) {
                  setErros({ senha: conferida.error.issues[0]?.message ?? 'Senha inválida.' })
                  return
                }
                void comEnvio(async () => {
                  const token = view === 'redefinir' ? (tokenDeReset ?? '') : (tokenDeConvite ?? '')
                  const r =
                    view === 'redefinir'
                      ? await api.redefinirSenha({ token, senha: senhaNova })
                      : await api.aceitarConvite({ token, senha: senhaNova })
                  // A identidade vem do token validado; nenhuma sessão parcial é criada.
                  setEmail(r.email)
                  setSenha(senhaNova)
                  setManterConectado(false)
                  await seguirDe(r.proximoPasso, { email: r.email, senha: senhaNova })
                })
              }}
              aoVoltarAoLogin={() => {
                window.location.replace('/entrar')
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

function Voltar({ aoVoltar }: { aoVoltar: () => void }) {
  return (
    <button
      type="button"
      onClick={aoVoltar}
      className="flex items-center gap-1.5 text-[13px] text-muted hover:text-navy mb-5"
    >
      <Icone nome="arrow-left" className="w-4 h-4" /> Voltar
    </button>
  )
}

function CampoDeCodigo({
  valor,
  erro,
  aoMudar,
}: {
  valor: string
  erro: string | undefined
  aoMudar: (valor: string) => void
}) {
  return (
    <Campo
      classe="md:!h-11"
      name="codigo"
      rotulo="Código de 6 dígitos"
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={12}
      value={valor}
      erro={erro}
      onChange={(e) => {
        aoMudar(e.target.value)
      }}
    />
  )
}

/**
 * Redefinir senha e aceitar convite: o mesmo formulário, dois títulos.
 *
 * São o mesmo ato — definir a senha a partir de um link — e a diferença é o que
 * acontece depois: o convite ainda ATIVA o acesso.
 */
function DefinicaoDeSenha({
  modo,
  senha,
  erro,
  banner,
  enviando,
  aoMudarSenha,
  aoEnviar,
  aoVoltarAoLogin,
}: {
  modo: 'redefinir' | 'convite'
  senha: string
  erro: string | undefined
  banner: string | undefined
  enviando: boolean
  aoMudarSenha: (valor: string) => void
  aoEnviar: () => void
  aoVoltarAoLogin: () => void
}) {
  const linkMorto = banner !== undefined && /link/i.test(banner)
  return (
    <section data-teste={modo === 'redefinir' ? 'view-redefinir' : 'view-convite'}>
      <p className="eyebrow text-muted mb-2">{modo === 'redefinir' ? 'Recuperação' : 'Bem-vindo'}</p>
      <h2 className={`text-[26px] font-semibold tracking-tight text-navy leading-tight ${modo === 'redefinir' ? '' : 'mb-7'}`}>
        {modo === 'redefinir' ? 'Escolha uma senha nova' : 'Defina a sua senha'}
      </h2>
      {/*
        Fica a consequência da redefinição (a senha antiga deixa de valer). O "É com ela que você
        entrará na plataforma" do convite saiu (D-203): o título "Defina a sua senha" já diz.
      */}
      {modo === 'redefinir' && (
        <p className="text-sm text-muted mt-1.5 mb-7">A senha atual deixa de valer assim que a nova for salva.</p>
      )}

      {banner !== undefined && <Banner>{banner}</Banner>}

      {linkMorto ? (
        <BotaoSecundario className="w-full min-h-[44px]" onClick={aoVoltarAoLogin}>
          Solicitar novo link
        </BotaoSecundario>
      ) : (
        <form
          className="space-y-4"
          noValidate
          onSubmit={(evento) => {
            evento.preventDefault()
            aoEnviar()
          }}
        >
          <Campo
            classe="md:!h-11"
            name="senha-nova"
            type="password"
            rotulo="Nova senha"
            autoComplete="new-password"
            value={senha}
            erro={erro}
            microcopy={erro === undefined ? `Mínimo de ${String(MINIMO_DA_SENHA)} caracteres. Misture letras, números e símbolos.` : undefined}
            onChange={(e) => {
              aoMudarSenha(e.target.value)
            }}
          />
          <BotaoPrimario
            type="submit"
            className="w-full min-h-[44px]"
            carregando={enviando}
            rotuloDeCarregamento="Salvando…"
          >
            {modo === 'redefinir' ? 'Salvar e entrar' : 'Definir senha e entrar'}
          </BotaoPrimario>
        </form>
      )}
    </section>
  )
}

/** O painel escuro da esquerda — visível só a partir de `lg`, como no protótipo. */
function PainelInk() {
  return (
    <div
      data-teste="painel-ink"
      className="hidden lg:flex flex-col justify-between bg-ink text-ondark relative overflow-hidden"
    >
      <div className="dot-grid-escuro absolute inset-0" />
      <div className="relative p-10">
        <img src="/marca/logo-digitalview-white.png" alt="DigitalView" style={{ height: 26, width: 'auto', display: 'block' }} />
      </div>
      <div className="relative px-10 pb-4 max-w-lg">
        <p className="eyebrow text-white/45 mb-4">Plataforma operacional · LatAm</p>
        <h1 className="text-[40px] font-semibold tracking-tight text-white leading-[1.05]">
          O fim da era
          <br />
          da <span className="italic">intuição</span>.
        </h1>
        <p className="text-white/55 mt-5 leading-relaxed text-[15px]">
          Comercial, mídia, faturamento e financeiro no mesmo fluxo. Cada número com origem
          rastreável até o pedido.
        </p>
      </div>
      <div className="relative p-10 text-white/35 text-[12px] flex items-center gap-4">
        <span>© 2026 DigitalView</span>
      </div>
    </div>
  )
}
